import {isEqual, last, uniq} from 'lodash';
import React, {FC, PropsWithChildren, RefObject, useCallback, useLayoutEffect, useRef, useState} from 'react';
import styled, {css} from 'styled-components';

import {buildAccessibleButtonProps} from '../../../../libs/shared/helpers/src/accessibility/accessibilityHelpers';
import {getWidthOfElement} from '../../../../libs/shared/helpers/src/browser/domHelpers';
import {buildInteractionIdProps} from '../../../../libs/shared/helpers/src/interaction/interactionHelpers';
import {usePrevious} from '../../../../libs/shared/helpers/src/react/hooks/hookHelpers';
import {ClickEventHandler, Handler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {ChromelessButton} from '../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon, IconName} from '../../../../libs/shared/ui/src/components/icon/icon';
import {VisuallyHidden} from '../../../../libs/shared/ui/src/components/visuallyHidden/visuallyHidden';
import {useSize} from '../../../../libs/shared/ui/src/hooks/sizeHooks';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {center} from '../../../../libs/shared/ui/src/styles/mixins';
import {buildIndexMap} from '../../../../srcCommon/helpers/itemIndexHelpers';
import {getSelectedIdsOnClick} from '../../../helpers/selectionHelpers';
import {InboxesFiltersEnum} from '../../../models/url/models/inboxesUrlModel';
import {InboxesParameters} from '../../../store/panels/inboxes/parameters/inboxesParameters';
import {areParametersCellsEqual} from '../../../store/panels/inboxes/parameters/inboxesParametersHelpers';
import {Filter, PaginatedFilterDefinition} from '../../inboxes/cells/header/cellsHeaderFilters';
import {GuidedDemoArchiveInfoBubble} from '../../infoBubbles/guidedDemoArchiveInfoBubble';

/*
 * Props.
 */

interface SegmentedFiltersPaginatedProps {
  parameters: InboxesParameters;
  filters: ReadonlyArray<PaginatedFilterDefinition>;
  selectedFilters: ReadonlyArray<Filter>;
}

interface RenderFiltersOptions {
  isHidden: boolean;
  visibleArchivedFilterRef: RefObject<HTMLDivElement>;
}

/*
 * Style.
 */

const filtersBarHeight = 22;
const navButtonSize = 22;
const filterItemsMargin = 3;

const StyledFiltersDiv = styled.div`
  display: flex;
  background: ${(p) => p.theme.foreground};

  /* Compensate for margins on the first/last items. */
  margin-left: -${filterItemsMargin}px;
  margin-right: -${filterItemsMargin}px;
`;

const StyledFiltersWrapperDiv = styled.div`
  flex-shrink: 1;
  flex-grow: 1;

  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  height: ${filtersBarHeight}px;
  overflow: hidden;
`;

const StyledHiddenFiltersWrapperDiv = styled(VisuallyHidden)`
  display: flex;
  flex-direction: row;
`;

const StyledFilterNavButton = styled(ChromelessButton)`
  width: ${navButtonSize}px;
  height: ${navButtonSize}px;
  border-radius: 50%;
  display: inline-block;
  flex-shrink: 0;
  margin: 0 ${filterItemsMargin}px;

  color: ${(p) => p.theme.greys.shade70};
  ${center()};

  &:hover {
    background: ${(p) => p.theme.alphas.gray10};
    color: ${(p) => p.theme.greys.shade80};
  }
`;

// The isolated prop is whether the item is selected or not
interface FilterItemStyleProps {
  $isSelected: boolean;
}
const StyledFilterItemDiv = styled.div<FilterItemStyleProps>`
  flex-shrink: 0;
  margin: 0 ${filterItemsMargin}px;
  padding: 4px 8px;
  border-radius: 100px;
  position: relative;

  font-size: ${fontSizes.verySmall};
  line-height: ${fontSizes.medium};
  font-weight: ${fontWeights.normal};

  ${(p) => addFilterItemStyles(p.$isSelected)};
`;

const StyledFilterBoldItemContentDiv = styled.div`
  font-weight: ${fontWeights.semibold};
  opacity: 0;
`;

const StyledFilterRegularItemContentDiv = styled.div<FilterItemStyleProps>`
  font-weight: ${(p) => (p.$isSelected ? fontWeights.semibold : fontWeights.medium)};
  position: absolute;
  width: calc(100% - 16px);
  text-align: center;
`;

function addFilterItemStyles(isSelected: boolean) {
  if (isSelected) {
    return css`
      background: ${(p) => p.theme.alphas.gray20};
      color: ${(p) => p.theme.greys.shade90};
    `;
  }

  return css`
    color: ${(p) => p.theme.greys.shade70};
    &:hover {
      background: ${(p) => p.theme.alphas.gray10};
      color: ${(p) => p.theme.greys.shade80};
    }
  `;
}

/*
 * Component.
 */

interface FilterWidth {
  width: number;
  index: number;
}

export const SegmentedFiltersPaginated: FC<PropsWithChildren<SegmentedFiltersPaginatedProps>> = (props) => {
  const {parameters, filters, selectedFilters} = props;

  const containerRef = useRef<HTMLDivElement>(null);
  const containerSizeInfo = useSize(containerRef);
  const containerWidth = (containerSizeInfo && containerSizeInfo.width) || null;
  const invisibleRenderRef = useRef<HTMLDivElement>(null);
  const [pages, setPages] = useState<Array<Array<FilterWidth>>>([]);
  const pageIndexOfLastSelectedFilter = findPageIndexOfLastSelectedFilter(filters, selectedFilters, pages);
  const pagesNumber = pages.length;
  const [currentPage, setCurrentPage] = useState<number>(1);
  const visibleArchivedFilterRef = useRef<HTMLDivElement>(null);
  const filterRenders = renderFilters(props);
  const shouldDisplayPreviousButton = currentPage > 1;
  const shouldDisplayNextButton = pagesNumber > 1 && currentPage < pagesNumber;
  const previousParameters = usePrevious(parameters);

  useLayoutEffect(() => {
    if (!invisibleRenderRef.current || !containerWidth) {
      return;
    }

    const rootElement = invisibleRenderRef.current;
    const children = Array.from(rootElement.children);
    const childrenWidths = children.map((e, index) => ({
      index,
      width: Math.ceil(getWidthOfElement(e)),
    }));

    const newPages = getPagesArray(childrenWidths, containerWidth);
    setPages((oldPages) => {
      // Avoid a cascading render.
      if (isEqual(oldPages, newPages)) {
        return oldPages;
      }

      return newPages;
    });

    // Find on which page the last filter is so we can select it.
    const newPageIndexOfLastSelectedFilter = findPageIndexOfLastSelectedFilter(
      filters,
      selectedFilters,
      newPages,
    );

    // Don't switch pages automatically unless the parameters change or the selected filter moves to another page.
    if (
      previousParameters &&
      areParametersCellsEqual(previousParameters, parameters) &&
      pageIndexOfLastSelectedFilter === newPageIndexOfLastSelectedFilter
    ) {
      return;
    }

    // We don't want to do that if we have more than one filter selected as it results in weird behaviors otherwise.
    if (selectedFilters.length === 1 && newPageIndexOfLastSelectedFilter > -1) {
      setCurrentPage(newPageIndexOfLastSelectedFilter + 1);
    } else {
      setCurrentPage(1);
    }
  }, [
    containerWidth,
    pageIndexOfLastSelectedFilter,
    previousParameters,
    parameters,
    filters,
    selectedFilters,
  ]);

  const moveToPreviousPage = useCallback(() => {
    setCurrentPage(Math.max(currentPage - 1, 1));
  }, [currentPage]);

  const moveToNextPage = useCallback(() => {
    setCurrentPage(Math.min(currentPage + 1, pagesNumber));
  }, [currentPage, pagesNumber]);

  const pageOfRenders = pages[currentPage - 1] || [];

  return (
    <StyledFiltersDiv ref={containerRef}>
      <StyledHiddenFiltersWrapperDiv ref={invisibleRenderRef}>
        {filterRenders({isHidden: true, visibleArchivedFilterRef})}
      </StyledHiddenFiltersWrapperDiv>
      {maybeRenderButton(shouldDisplayPreviousButton, 'chevronLeftSmall', moveToPreviousPage, true)}
      <StyledFiltersWrapperDiv>
        {pageOfRenders.map(({index}) => filterRenders({isHidden: false, visibleArchivedFilterRef})[index])}
      </StyledFiltersWrapperDiv>
      {maybeRenderButton(shouldDisplayNextButton, 'chevronRightSmall', moveToNextPage, false)}
    </StyledFiltersDiv>
  );
};

const navButtonRealEstate = navButtonSize + filterItemsMargin * 2;

function getPagesArray(filterWidths: Array<FilterWidth>, maxWidthPerPage: number) {
  const pages: Array<Array<FilterWidth>> = [];
  let currentPage: Array<FilterWidth> = [];
  let currentPageWidth = 0;

  filterWidths.forEach((filter) => {
    let currentPageRemainingWidth = maxWidthPerPage - currentPageWidth;

    // We need a previous button on this page.
    if (pages.length > 0) {
      currentPageRemainingWidth -= navButtonRealEstate;
    }

    // We need a next button on this page.
    if (filter.index < filterWidths.length - 1) {
      currentPageRemainingWidth -= navButtonRealEstate;
    }

    // Do we need a new page?
    const width = filter.width + filterItemsMargin * 2;
    if (width > currentPageRemainingWidth) {
      pages.push(currentPage);
      currentPage = [];
      currentPageWidth = 0;
    }

    currentPage.push(filter);
    // Add the necessary margins into calculation.
    currentPageWidth += width;
  });

  if (currentPage.length > 0) {
    pages.push(currentPage);
  }

  return pages;
}

function findPageIndexOfLastSelectedFilter(
  filters: ReadonlyArray<PaginatedFilterDefinition>,
  selectedFilters: ReadonlyArray<Filter>,
  pages: ReadonlyArray<ReadonlyArray<FilterWidth>>,
): number {
  const lastSelectedFilter = last(selectedFilters);
  const lastSelectedFilterIndex = filters.findIndex((f) => f.id === lastSelectedFilter);
  return pages.findIndex((p) => p.some((f) => f.index === lastSelectedFilterIndex));
}

function maybeRenderButton(shouldRender: boolean, name: IconName, onClick: Handler, isPrevious: boolean) {
  if (!shouldRender) {
    return null;
  }

  return (
    <StyledFilterNavButton onClick={onClick}>
      <Icon name={name} />
    </StyledFilterNavButton>
  );
}

function renderFilters(props: SegmentedFiltersPaginatedProps) {
  const {filters, selectedFilters} = props;
  return (options: RenderFiltersOptions) =>
    filters.map((f) => {
      const isSelected = selectedFilters.includes(f.id);

      // We need to add a ref to the visible archived filter to make the info bubble work.
      const isVisibleArchivedFilter = !options.isHidden && f.id === InboxesFiltersEnum.ARCHIVED;

      return (
        <StyledFilterItemDiv
          ref={isVisibleArchivedFilter ? options.visibleArchivedFilterRef : undefined}
          $isSelected={isSelected}
          key={f.id}
          {...makeFilterButtonProps(f, props, isSelected)}
        >
          <StyledFilterRegularItemContentDiv $isSelected={isSelected}>
            {f.content}
          </StyledFilterRegularItemContentDiv>
          <StyledFilterBoldItemContentDiv aria-hidden="true">{f.content}</StyledFilterBoldItemContentDiv>
          {isVisibleArchivedFilter && (
            <GuidedDemoArchiveInfoBubble targetRef={options.visibleArchivedFilterRef} />
          )}
        </StyledFilterItemDiv>
      );
    });
}

function makeFilterButtonProps(
  filterDefinition: PaginatedFilterDefinition,
  props: SegmentedFiltersPaginatedProps,
  isSelected: boolean,
) {
  const onClick: ClickEventHandler = (e) => {
    if (!filterDefinition.onMultipleFiltersSelect) {
      return;
    }

    const filterIds = props.filters.map((f) => f.id);
    const filtersIndexes = buildIndexMap(filterIds);
    const newFilters = uniq(
      getSelectedIdsOnClick<Filter>(props.selectedFilters, filterIds, filtersIndexes, filterDefinition.id, e),
    );
    filterDefinition.onMultipleFiltersSelect(newFilters);
  };

  return {
    ...buildAccessibleButtonProps({
      onClick,
    }),
    role: 'checkbox',
    'aria-checked': isSelected,
    ...buildInteractionIdProps(filterDefinition.id),
  };
}
